import {
    DefaultObservableValue,
    Errors,
    MutableObservableOption,
    Observer,
    Option,
    Progress,
    RuntimeNotifier,
    Terminable,
    UUID
} from "@opendaw/lib-std"
import {ProjectDialogs} from "@/project/ProjectDialogs"
import {Dialogs} from "@/ui/components/dialogs"
import {Promises} from "@opendaw/lib-runtime"
import {Files} from "@opendaw/lib-dom"
import {
    FilePickerAcceptTypes,
    OfflineEngineRenderer,
    Project,
    ProjectBundle,
    ProjectEnv,
    ProjectMeta,
    ProjectMigration,
    ProjectProfile,
    ProjectStorage,
    RemoteProjectAPI,
    SampleService,
    SoundfontService
} from "@opendaw/studio-core"
import {ProjectSkeleton, SampleLoaderManager, SoundfontLoaderManager} from "@opendaw/studio-adapters"
import {AudioData, WavFile} from "@opendaw/lib-dsp"
import {BoxGraph} from "@opendaw/lib-box"
import {BoxIO} from "@opendaw/studio-boxes"

export class ProjectProfileService {
    readonly #profile: MutableObservableOption<ProjectProfile>

    readonly #env: ProjectEnv
    readonly #sampleService: SampleService
    readonly #sampleManager: SampleLoaderManager
    readonly #soundfontService: SoundfontService
    readonly #soundfontManager: SoundfontLoaderManager

    constructor({env, sampleService, sampleManager, soundfontService, soundfontManager}: {
        env: ProjectEnv,
        sampleService: SampleService,
        sampleManager: SampleLoaderManager,
        soundfontService: SoundfontService,
        soundfontManager: SoundfontLoaderManager
    }) {
        this.#env = env
        this.#sampleService = sampleService
        this.#sampleManager = sampleManager
        this.#soundfontService = soundfontService
        this.#soundfontManager = soundfontManager
        this.#profile = new MutableObservableOption<ProjectProfile>()
    }

    getValue(): Option<ProjectProfile> {return this.#profile}
    setValue(value: Option<ProjectProfile>): void {this.#profile.wrapOption(value)}
    subscribe(observer: Observer<Option<ProjectProfile>>): Terminable {
        return this.#profile.subscribe(observer)
    }
    catchupAndSubscribe(observer: Observer<Option<ProjectProfile>>): Terminable {
        observer(this.#profile)
        return this.#profile.subscribe(observer)
    }

    async save(): Promise<void> {
        return this.#profile.ifSome(profile => profile.saved() ? profile.save() : this.saveAs())
    }

    async saveAs(): Promise<void> {
        return this.#profile.ifSome(async profile => {
            const {status, value: meta} = await Promises.tryCatch(ProjectDialogs.showSaveDialog({
                headline: "Save Project",
                meta: profile.meta
            }))
            if (status === "rejected") {return}
            const optProfile = await profile.saveAs(meta)
            optProfile.ifSome(profile => this.#profile.wrap(profile))
        })
    }

    async load(uuid: UUID.Bytes, meta: ProjectMeta) {
        const {status, value: project, error} = await Promises.tryCatch(
            ProjectStorage.loadProject(uuid).then(buffer => Project.loadAnyVersion(this.#env, buffer)))
        if (status === "rejected") {
            await RuntimeNotifier.info({headline: "Could not load project", message: String(error)})
            return
        }
        await this.#sampleService.replaceMissingFiles(project.boxGraph, this.#sampleManager)
        await this.#soundfontService.replaceMissingFiles(project.boxGraph, this.#soundfontManager)
        const cover = await ProjectStorage.loadCover(uuid)
        this.#setProfile(uuid, project, meta, cover, true)
    }

    async exportBundle() {
        return this.#profile.ifSome(async profile => {
            const progressValue = new DefaultObservableValue(0.0)
            const processDialog = RuntimeNotifier.progress({headline: "Bundling Project...", progress: progressValue})
            const {status, value: arrayBuffer, error} = await Promises.tryCatch(
                ProjectBundle.encode(profile, progress => progressValue.setValue(progress)))
            processDialog.terminate()
            if (status === "rejected") {
                await RuntimeNotifier.info({headline: "Export Failed", message: String(error)})
                return
            }
            const {status: approveStatus} = await Promises.tryCatch(Dialogs.approve({
                headline: "Save Project Bundle",
                message: "",
                approveText: "Save"
            }))
            if (approveStatus === "rejected") {return}
            try {
                await Files.save(arrayBuffer, {
                    suggestedName: `${profile.meta.name}.odb`,
                    types: [FilePickerAcceptTypes.ProjectBundleFileType],
                    startIn: "desktop"
                })
            } catch (error) {
                if (!Errors.isAbort(error)) {
                    Dialogs.info({headline: "Could not export project", message: String(error)}).finally()
                }
            }
        })
    }

    async importBundle() {
        try {
            const [file] = await Files.open({types: [FilePickerAcceptTypes.ProjectBundleFileType]})
            const arrayBuffer = await file.arrayBuffer()
            const exclude = this.#profile.map(({uuid}) => uuid).unwrapOrUndefined()
            const profile = await ProjectBundle.decode(this.#env, arrayBuffer, exclude)
            this.#profile.wrap(profile)
        } catch (error) {
            if (!Errors.isAbort(error)) {
                Dialogs.info({headline: "Could not load project", message: String(error)}).finally()
            }
        }
    }

    async saveToCloud(): Promise<void> {
        return this.#profile.ifSome(async profile => {
            if (profile.meta.artist.trim() === "") {
                const {status, value: names} = await Promises.tryCatch(
                    ProjectDialogs.showOpslaanDialog(profile.meta))
                if (status === "rejected") {return}
                profile.updateMetaData("artist", names.artist)
                profile.updateMetaData("name", names.name)
            }
            const {artist, name} = profile.meta
            const progressValue = new DefaultObservableValue(0.0)
            const processDialog = RuntimeNotifier.progress({headline: "Project opslaan...", progress: progressValue})
            const {status, value: arrayBuffer, error} = await Promises.tryCatch(
                ProjectBundle.encode(profile, progress => progressValue.setValue(progress)))
            processDialog.terminate()
            if (status === "rejected") {
                await RuntimeNotifier.info({headline: "Opslaan mislukt", message: String(error)})
                return
            }
            const {status: uploadStatus, error: uploadError} = await Promises.tryCatch(
                RemoteProjectAPI.upload(UUID.toString(profile.uuid), arrayBuffer, {artist, name}))
            if (uploadStatus === "rejected") {
                await RuntimeNotifier.info({headline: "Upload mislukt", message: String(uploadError)})
                return
            }
            const enableMp3Preview: boolean = false
            const loopArea = profile.project.timelineBox.loopArea
            const loopStart = loopArea.from.getValue()
            const loopEnd = loopArea.to.getValue()
            if (enableMp3Preview && loopEnd > loopStart) {
                const renderDialog = RuntimeNotifier.progress({headline: "Preview renderen..."})
                const mp3Result = await Promises.tryCatch((async () => {
                    const sampleRate = this.#sampleService.audioContext.sampleRate
                    const durationSeconds = profile.project.tempoMap.intervalToSeconds(loopStart, loopEnd)
                    const numSamples = Math.ceil(durationSeconds * sampleRate)
                    const renderProject = profile.project.copy()
                    renderProject.boxGraph.beginTransaction()
                    renderProject.timelineBox.loopArea.enabled.setValue(false)
                    renderProject.boxGraph.endTransaction()
                    const renderer = await OfflineEngineRenderer.create(renderProject, Option.None, sampleRate)
                    const renderResult = await Promises.tryCatch((async () => {
                        await renderer.waitForLoading()
                        renderer.setPosition(loopStart)
                        renderer.play()
                        await renderer.waitForLoading()
                        const channels = await renderer.step(numSamples)
                        const data = AudioData.create(sampleRate, numSamples, 2)
                        data.frames[0].set(channels[0])
                        data.frames[1].set(channels[1])
                        return data
                    })())
                    renderer.terminate()
                    if (renderResult.status === "rejected") {return Promise.reject(renderResult.error)}
                    const {FFmpegWorker} = await Promises.guardedRetry(() =>
                        import("@opendaw/studio-core/FFmpegWorker"), (_, count) => count < 10)
                    const worker = await FFmpegWorker.load(Progress.Empty)
                    const mp3ArrayBuffer = await worker.mp3Converter()
                        .convert(new Blob([WavFile.encodeFloats(renderResult.value)]), Progress.Empty)
                    await RemoteProjectAPI.uploadMp3(UUID.toString(profile.uuid), mp3ArrayBuffer, {artist, name})
                })())
                renderDialog.terminate()
                if (mp3Result.status === "rejected") {
                    console.warn("MP3 preview upload failed:", mp3Result.error)
                }
            }
            await RuntimeNotifier.info({headline: "Opgeslagen!", message: `${artist} — ${name}`})
        })
    }

    async setDefaultProject(): Promise<void> {
        return this.#profile.ifSome(async profile => {
            const {status: passwordStatus, value: password} = await Promises.tryCatch(
                ProjectDialogs.showPasswordDialog("Set Default Project"))
            if (passwordStatus === "rejected") {return}
            const strippedProfile = new ProjectProfile(profile.uuid, profile.project.copy(),
                ProjectMeta.copy(profile.meta), profile.cover)
            strippedProfile.updateMetaData("name", "")
            strippedProfile.updateMetaData("artist", "")
            const progressValue = new DefaultObservableValue(0.0)
            const processDialog = RuntimeNotifier.progress({headline: "Default project opslaan...", progress: progressValue})
            const {status, value: arrayBuffer, error} = await Promises.tryCatch(
                ProjectBundle.encode(strippedProfile, progress => progressValue.setValue(progress)))
            processDialog.terminate()
            if (status === "rejected") {
                await RuntimeNotifier.info({headline: "Opslaan mislukt", message: String(error)})
                return
            }
            const {status: uploadStatus, error: uploadError} = await Promises.tryCatch(
                RemoteProjectAPI.uploadDefault(arrayBuffer, password))
            if (uploadStatus === "rejected") {
                const message = String(uploadError).includes("unauthorized") ? "Verkeerd wachtwoord" : String(uploadError)
                await RuntimeNotifier.info({headline: "Instellen mislukt", message})
                return
            }
            await RuntimeNotifier.info({headline: "Default project ingesteld!", message: ""})
        })
    }

    async loadFromCloud(): Promise<void> {
        const {status: passwordStatus, value: password} = await Promises.tryCatch(
            ProjectDialogs.showPasswordDialog("Load from Cloud"))
        if (passwordStatus === "rejected") {return}
        const {status: listStatus, value: entries, error: listError} = await Promises.tryCatch(
            RemoteProjectAPI.list(password))
        if (listStatus === "rejected") {
            const message = String(listError).includes("unauthorized") ? "Verkeerd wachtwoord" : String(listError)
            await RuntimeNotifier.info({headline: "Laden mislukt", message})
            return
        }
        const {status, value: uuid} = await Promises.tryCatch(ProjectDialogs.showCloudBrowseDialog(entries))
        if (status === "rejected") {return}
        const {status: downloadStatus, value: arrayBuffer, error: downloadError} = await Promises.tryCatch(
            RemoteProjectAPI.download(uuid, password))
        if (downloadStatus === "rejected") {
            const message = String(downloadError).includes("unauthorized") ? "Verkeerd wachtwoord" : String(downloadError)
            await RuntimeNotifier.info({headline: "Download mislukt", message})
            return
        }
        const exclude = this.#profile.map(({uuid: profileUUID}) => profileUUID).unwrapOrUndefined()
        const {status: decodeStatus, value: profile, error: decodeError} = await Promises.tryCatch(
            ProjectBundle.decode(this.#env, arrayBuffer, exclude))
        if (decodeStatus === "rejected") {
            await RuntimeNotifier.info({headline: "Kon project niet laden", message: String(decodeError)})
            return
        }
        this.#profile.wrap(profile)
    }

    async saveFile() {
        this.#profile.ifSome(async profile => {
            const arrayBuffer = profile.project.toArrayBuffer() as ArrayBuffer
            try {
                const fileName = await Files.save(arrayBuffer, {
                    suggestedName: "project.od",
                    types: [FilePickerAcceptTypes.ProjectFileType]
                })
                Dialogs.info({message: `Project '${fileName}' saved successfully!`}).finally()
            } catch (error) {
                if (!Errors.isAbort(error)) {
                    Dialogs.info({message: `Error saving project: ${error}`}).finally()
                }
            }
        })
    }

    async loadFile() {
        try {
            const [file] = await Files.open({
                types: [
                    FilePickerAcceptTypes.ProjectFileType, FilePickerAcceptTypes.JsonFileType
                ]
            })
            if (file.name.endsWith(".json")) {
                console.debug("importing json file")
                const jsonString = await file.text()
                const json = JSON.parse(jsonString)
                console.debug("parsed json", json)
                const boxGraph = new BoxGraph<BoxIO.TypeMap>(Option.wrap(BoxIO.create))
                boxGraph.fromJSON(json, false)
                boxGraph.debugBoxes()
                const mandatoryBoxes = ProjectSkeleton.findMandatoryBoxes(boxGraph)
                const skeleton: ProjectSkeleton = {boxGraph, mandatoryBoxes}
                await ProjectMigration.migrate(this.#env, skeleton)
                boxGraph.verifyPointers()
                const project = Project.fromSkeleton(this.#env, skeleton, false)
                this.setProject(project, file.name)
            } else {
                const project = await Project.loadAnyVersion(this.#env, await file.arrayBuffer())
                this.#setProfile(UUID.generate(), project, ProjectMeta.init(file.name), Option.None)
            }
        } catch (error) {
            if (!Errors.isAbort(error)) {
                Dialogs.info({headline: "Could not load json", message: String(error)}).finally()
            }
        }
    }

    setProject(project: Project, name: string): void {
        this.#setProfile(UUID.generate(), project, ProjectMeta.init(name), Option.None)
    }

    #setProfile(...args: ConstructorParameters<typeof ProjectProfile>): void {
        this.#profile.wrap(this.#createProfile(...args))
    }

    #createProfile(...args: ConstructorParameters<typeof ProjectProfile>): ProjectProfile {
        return new ProjectProfile(...args)
    }
}